import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Monitor,
  Lock,
  CheckCircle,
  Zap,
  BookOpen,
  GitBranch,
  FileText,
} from "lucide-react";

const FEATURES = [
  "One-click browser attack box plus per-session VPN access",
  "Guided questions with attempt limits, TKSA scoring, and reveal-answer",
  "Walkthroughs that unlock once you've attempted the challenge",
  "Auto-generated blue-team response & remediation runbooks",
  "The scenario's network on an embedded, auto-laid-out canvas",
  "Request a custom box tailored to any vulnerability you need",
];

const TABS = [
  "Play machine",
  "Walkthroughs",
  "Remediation",
  "Topology",
  "Request a box",
];
const CHROME = [
  "lab-session · phantom-ticket",
  "walkthroughs · unlocking",
  "remediation · runbook",
  "topology · live",
  "request · custom box",
];

const ROTATE_MS = 4200;
const R = 21;
const CIRC = 2 * Math.PI * R; // ≈ 131.9

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduce;
}

/* ── Tab 0 · Play machine ────────────────────────────────────────────────── */
function PlayMachine({ typed, solved, pct }) {
  const offset = CIRC * (1 - pct / 100);
  return (
    <>
      <div className="row">
        <div className="mrun">
          <span className="pulse" />
          <div>
            <div className="t">Machine running</div>
            <div className="m qv-mono">
              scenario-vm · <b>172.16.0.5</b>
            </div>
          </div>
        </div>
        <span className="mbtn fill qv-mono">
          <Monitor />
          AttackBox
        </span>
      </div>

      <div className={`qwrap ${solved ? "solved" : ""}`}>
        <div className="qtop">
          <span className="lbl qv-mono">Question 3 of 6</span>
          <span className="att qv-mono">Attempt 2 / 3</span>
        </div>
        <p className="q">Which service exposed the initial foothold?</p>
        <div className="ans">
          <div className={`field qv-mono ${solved ? "solved" : ""}`}>
            <span>{typed}</span>
            <span className="caret" />
          </div>
          <div className={`go ${solved ? "pop" : ""}`}>
            <Check />
          </div>
        </div>
      </div>

      <div className="tksa">
        <div className="donut">
          <svg width="52" height="52" viewBox="0 0 52 52">
            <circle
              cx="26"
              cy="26"
              r={R}
              fill="none"
              stroke="var(--color-border-solid)"
              strokeWidth="5"
            />
            <circle
              cx="26"
              cy="26"
              r={R}
              fill="none"
              stroke="var(--color-primary)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={offset}
              transform="rotate(-90 26 26)"
              style={{
                filter: "drop-shadow(0 0 5px rgba(34,197,94,.6))",
                transition: "stroke-dashoffset .12s linear",
              }}
            />
          </svg>
          <span className="val qv-mono">{pct}%</span>
        </div>
        <div className="info">
          <div className="t">TKSA coverage</div>
          <div className="chips qv-mono">
            <span style={{ animationDelay: ".2s" }}>T1190</span>
            <span style={{ animationDelay: ".4s" }}>T1046</span>
            <span style={{ animationDelay: ".6s" }}>T1078</span>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Tab 1 · Walkthroughs ────────────────────────────────────────────────── */
function Walkthroughs() {
  const steps = [
    ["Foothold & enumeration", "section 01 · 6 steps"],
    ["Exploiting the ticket preview", "section 02 · 9 steps"],
    ["Privilege pivot to moderator", "section 03 · 5 steps"],
    ["Signed incident confirmation", "section 04 · 3 steps"],
  ];
  return (
    <>
      {steps.map(([t, s]) => (
        <div className="step" key={t}>
          <span className="lock">
            <Lock />
          </span>
          <div className="txt">
            {t}
            <small className="qv-mono">{s}</small>
          </div>
          <span className="tick qv-mono">unlocked</span>
        </div>
      ))}
    </>
  );
}

/* ── Tab 2 · Remediation ─────────────────────────────────────────────────── */
function Remediation() {
  const lines = [
    ["Patch reflected-XSS in ticket preview renderer", ".1s"],
    ["Enforce CSP + output encoding on API surface", ".5s"],
    ["Add detection rule for moderator-action anomalies", ".9s"],
    ["Rotate signing keys & revoke test sessions", "1.3s"],
  ];
  return (
    <div className="play">
      <h4 className="qv-mono">Blue-team runbook · auto-generated</h4>
      {lines.map(([txt, d]) => (
        <div className="pline qv-mono" key={txt} style={{ "--d": d }}>
          <span className="pcheck">
            <Check />
          </span>
          {txt}
        </div>
      ))}
    </div>
  );
}

/* ── Tab 3 · Topology ────────────────────────────────────────────────────── */
function Topology() {
  return (
    <div className="topo-wrap">
      <span className="caption qv-mono">scenario network · auto-laid-out</span>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 400 280"
        preserveAspectRatio="xMidYMid meet"
      >
        <path
          id="le-e1"
          d="M70 140 L180 70"
          stroke="var(--color-border-solid)"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          id="le-e2"
          d="M70 140 L180 210"
          stroke="var(--color-border-solid)"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          id="le-e3"
          d="M180 70 L310 140"
          stroke="var(--color-border-solid)"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          id="le-e4"
          d="M180 210 L310 140"
          stroke="var(--color-border-solid)"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          id="le-e5"
          d="M180 70 L180 210"
          stroke="var(--color-border)"
          strokeWidth="1.5"
          fill="none"
          strokeDasharray="4 4"
        />
        <circle
          className="nring"
          cx="70"
          cy="140"
          r="11"
          fill="var(--color-primary)"
        />
        <circle cx="70" cy="140" r="6" fill="var(--color-primary)" />
        <circle cx="180" cy="70" r="5.5" fill="#3a5a48" />
        <circle cx="180" cy="210" r="5.5" fill="#3a5a48" />
        <circle
          className="nring"
          cx="310"
          cy="140"
          r="11"
          fill="var(--color-primary)"
          style={{ animationDelay: "1s" }}
        />
        <circle cx="310" cy="140" r="6" fill="var(--color-primary)" />
        <circle r="3.2" fill="#3ee07a">
          <animateMotion dur="2.2s" repeatCount="indefinite">
            <mpath href="#le-e1" />
          </animateMotion>
        </circle>
        <circle r="3.2" fill="#3ee07a">
          <animateMotion dur="2.6s" repeatCount="indefinite" begin="0.6s">
            <mpath href="#le-e3" />
          </animateMotion>
        </circle>
        <circle r="3.2" fill="#5b9df0">
          <animateMotion dur="2.4s" repeatCount="indefinite" begin="1s">
            <mpath href="#le-e2" />
          </animateMotion>
        </circle>
        <circle r="3.2" fill="#5b9df0">
          <animateMotion dur="2.8s" repeatCount="indefinite" begin="0.3s">
            <mpath href="#le-e4" />
          </animateMotion>
        </circle>
        <text className="node-label" x="52" y="164">
          attacker
        </text>
        <text className="node-label" x="160" y="56">
          web-vm
        </text>
        <text className="node-label" x="158" y="230">
          api-vm
        </text>
        <text className="node-label" x="292" y="164">
          db-vm
        </text>
      </svg>
    </div>
  );
}

/* ── Tab 4 · Request a box ───────────────────────────────────────────────── */
function RequestBox() {
  const cards = [
    {
      Icon: CheckCircle,
      t: "Customized content",
      d: "Built for your chosen vulnerability",
    },
    { Icon: Zap, t: "Fast delivery", d: "Generated and deployed quickly" },
    { Icon: BookOpen, t: "Full walkthroughs", d: "Detailed guides included" },
  ];
  const how = [
    ["01 · submit", "Describe the CVE"],
    ["02 · build", "We craft the box"],
    ["03 · learn", "Start hacking"],
  ];
  return (
    <div>
      <p className="req-title qv-serif">Request a custom box</p>
      <p className="req-sub">
        Need a specific vulnerability covered? We'll build a tailored box
        designed around your requirements.
      </p>
      <div className="req-cards">
        {cards.map((c, i) => {
          const Icon = c.Icon;
          return (
            <div
              className="req-card"
              key={c.t}
              style={{ "--d": `${0.12 + i * 0.13}s` }}
            >
              <div className="ci">
                <Icon />
              </div>
              <div className="ct">{c.t}</div>
              <div className="cd">{c.d}</div>
            </div>
          );
        })}
      </div>
      <div className="req-btns">
        <button className="req-btn" style={{ "--d": ".52s" }}>
          <GitBranch />
          Revision (topology)
        </button>
        <button className="req-btn line" style={{ "--d": ".66s" }}>
          <FileText />
          Revision (description)
        </button>
      </div>
      <div className="req-how">
        {how.map(([b, txt], i) => (
          <div
            className="s qv-mono"
            key={b}
            style={{ "--d": `${0.82 + i * 0.12}s` }}
          >
            <b>{b}</b>
            {txt}
          </div>
        ))}
      </div>
    </div>
  );
}

const PANELS = [PlayMachine, Walkthroughs, Remediation, Topology, RequestBox];

export default function LabExperience() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = usePrefersReducedMotion();
  const [typed, setTyped] = useState("");
  const [solved, setSolved] = useState(false);
  const [pct, setPct] = useState(0);
  const timer = useRef();

  // auto-rotate until a tab is clicked
  useEffect(() => {
    if (reduce || paused) return;
    timer.current = setTimeout(
      () => setActive((a) => (a + 1) % TABS.length),
      ROTATE_MS,
    );
    return () => clearTimeout(timer.current);
  }, [active, paused, reduce]);

  // play-machine panel: type the answer + fill the coverage donut
  useEffect(() => {
    if (active !== 0) return;
    if (reduce) {
      setTyped("smtp-relay");
      setSolved(true);
      setPct(82);
      return;
    }
    setTyped("");
    setSolved(false);
    setPct(0);
    const word = "smtp-relay";
    let i = 0;
    const type = setInterval(() => {
      i += 1;
      setTyped(word.slice(0, i));
      if (i >= word.length) {
        clearInterval(type);
        setTimeout(() => setSolved(true), 450);
      }
    }, 110);
    let v = 0;
    const count = setInterval(() => {
      v += 3;
      if (v >= 82) {
        v = 82;
        clearInterval(count);
      }
      setPct(v);
    }, 40);
    return () => {
      clearInterval(type);
      clearInterval(count);
    };
  }, [active, reduce]);

  const Panel = PANELS[active];

  return (
    <section id="labs" className="lab-exp relative py-16 lg:py-24">
      <div className="mx-auto grid max-w-350 grid-cols-1 items-center gap-14 px-5 md:px-10 lg:grid-cols-[1fr_1.08fr]">
        {/* copy */}
        <div className="lp-reveal lp-left max-w-xl">
          <div className="eyebrow mb-5 flex items-center gap-3 qv-mono text-[11px] font-medium uppercase tracking-[0.28em] text-(--color-primary)">
            <span className="h-px w-9 bg-[linear-gradient(90deg,transparent,var(--color-primary))]" />
            The Lab Experience
          </div>
          <h1
            className="lp-rise qv-glitch mt-6 text-[32px] sm:text-[40px] leading-tight font-normal tracking-tight text-(--color-text-primary)"
            style={{ "--lp-delay": "90ms" }}
          >
            {" "}
            Every lab is a complete{" "}
            <span className="lp-highlight">workspace</span>
          </h1>
          <p className="lead mt-4 max-w-[46ch] text-[16px] leading-relaxed text-(--color-text-secondary)"></p>
          <ul className="feat mt-7">
            {FEATURES.map((f) => (
              <li key={f}>
                <span className="check">
                  <Check size={12} strokeWidth={2.4} />
                </span>
                {f}
              </li>
            ))}
          </ul>
          <Link
            to="/register"
            className="cta mt-2 inline-flex items-center gap-2.5 py-3 qv-mono text-[14px] font-medium text-(--color-primary)"
          >
            Start a lab
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* preview */}
        <div className="lp-reveal lp-right preview relative">
          <div className="card lp-float">
            <div className="chrome qv-mono text-[11px] text-(--color-text-muted)">
              <span className="lights">
                <i />
                <i />
                <i />
              </span>
              <span>{CHROME[active]}</span>
              <span className="livedot" />
            </div>
            <div className="tabs">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => {
                    setPaused(true);
                    setActive(i);
                  }}
                  className={`tab qv-mono text-[11px] uppercase tracking-wider ${i === active ? "on" : ""}`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="stage">
              <div key={active} className="panel">
                {active === 0 ? (
                  <PlayMachine typed={typed} solved={solved} pct={pct} />
                ) : (
                  <Panel />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
